import { useMutation, useQueryClient } from '@tanstack/react-query';

import { installLibraryItem, updateLibraryItem } from '@/lib/api/generated/api';
import type {
  ImportSubstitutions,
  LibraryInstallResult,
  LibraryItemKind,
} from '@/lib/api/generated/model';
import { invalidateWorkspace } from '@/features/workspaces';
import { invalidateWorkspaceFiles } from '@/features/workspace-files';
import { setInstallReport } from '../installReports';
import { libraryKeys } from '../queryKeys';

type Action =
  | {
      action: 'install';
      kind: LibraryItemKind;
      key: string;
      substitutions?: ImportSubstitutions;
    }
  | { action: 'update'; kind: LibraryItemKind; key: string };

/** The service's refusal envelope, when the body carries one. */
function refusalMessage(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data !== null) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
    const violations = (data as { violations?: unknown }).violations;
    if (Array.isArray(violations) && violations.length > 0) {
      const first = violations[0] as { message?: unknown };
      if (typeof first.message === 'string') return first.message;
    }
  }
  return fallback;
}

/**
 * Installing and updating, as one interface: an item and a name in, per-item
 * outcomes out.
 *
 * Two things a caller does not have to know: that a workflow install also
 * changes the workspace's workflows, so that cache is refreshed only when a
 * workflow came back; and that a *blocked* answer — the workflow names what
 * this deployment lacks — is a result to show, not an error, and invalidates
 * nothing because nothing was written (spec 104 FR-014).
 *
 * Every answer is also written to the report store, so it survives the reader
 * following one of its own links (spec 108 FR-005). A blocked answer is written
 * too, and before the early return: it is the report for that item, and a reader
 * who leaves and comes back must still be told what this deployment lacks rather
 * than shown an empty outcome list (FR-008).
 */
export function useInstallLibraryItem(workspaceId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: Action): Promise<LibraryInstallResult> => {
      const response =
        input.action === 'install'
          ? await installLibraryItem(workspaceId, {
              kind: input.kind,
              key: input.key,
              ...(input.substitutions
                ? { substitutions: input.substitutions }
                : {}),
            })
          : await updateLibraryItem(workspaceId, {
              kind: input.kind,
              key: input.key,
            });
      if (response.status === 200) return response.data;
      throw new Error(
        refusalMessage(
          response.data,
          input.action === 'install'
            ? 'The install could not complete.'
            : 'The update could not complete.',
        ),
      );
    },
    onSuccess: async (result, input) => {
      setInstallReport(
        workspaceId,
        { kind: input.kind, key: input.key },
        result,
      );
      if (result.blocked) return;
      await queryClient.invalidateQueries({
        queryKey: libraryKeys.items(workspaceId),
      });
      // An agent or a skill is a folder, so the files page's listings are
      // wrong the moment one lands — and it is a different page, which had
      // already cached them.
      await invalidateWorkspaceFiles(queryClient, workspaceId);
      if (result.workflow) await invalidateWorkspace(queryClient, workspaceId);
    },
  });

  return {
    install: (
      kind: LibraryItemKind,
      key: string,
      substitutions?: ImportSubstitutions,
    ) => mutation.mutate({ action: 'install', kind, key, substitutions }),
    update: (kind: LibraryItemKind, key: string) =>
      mutation.mutate({ action: 'update', kind, key }),
    pending: mutation.isPending,
    lastResult: mutation.data,
    error: mutation.error?.message,
    reset: mutation.reset,
  };
}
